import { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { Mail, Trash2 } from "lucide-react";
import { roleApi, userApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { useOrgId } from "@/hooks/useOrgId";
import type { RolePermissions, User } from "@/types";

interface InviteForm {
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role_id: string;
}

export default function UsersPage() {
  const user = useAuthStore((s) => s.user)!;
  const orgId = useOrgId();
  const canInvite = user.is_staff || !!user.role?.can_create_users;
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<RolePermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const form = useForm<InviteForm>();

  const load = async () => {
    setLoading(true);
    const [u, r] = await Promise.all([userApi.listForOrg(orgId), roleApi.list(orgId)]);
    setUsers(u);
    setRoles(r);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, [orgId]);

  const submit = form.handleSubmit(async (values) => {
    try {
      await userApi.invite({ ...values, organisation_id: orgId });
      toast.success("Invite sent");
      setOpen(false);
      form.reset();
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  const toggleActive = async (u: User) => {
    try {
      await userApi.update(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? "User deactivated" : "User activated");
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const resendInvite = async (u: User) => {
    try {
      const res = await userApi.resendInvite(u.id);
      toast.success(res.message ?? "Invite resent");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const deleteUser = async (u: User) => {
    const ok = window.confirm(
      `Delete ${u.email}?\n\n` +
      `This removes them from UmdoniWay AND from the External ID directory. ` +
      `Their audit log entries will remain (without their name). This action cannot be undone.`
    );
    if (!ok) return;
    try {
      await userApi.delete(u.id);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const columns: Column<User>[] = [
    {
      key: "name",
      header: "Name",
      render: (u) => (
        <div>
          <div className="font-medium">
            {u.first_name || u.last_name ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() : u.email}
          </div>
          <div className="text-xs text-slate-500">{u.email}</div>
        </div>
      ),
    },
    { key: "role", header: "Role", render: (u) => u.role?.name ?? "—" },
    {
      key: "active",
      header: "Status",
      render: (u) =>
        u.is_active ? (
          <span className="badge bg-brand-50 text-brand-800">Active</span>
        ) : (
          <span className="badge bg-slate-100 text-slate-600">Inactive</span>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (u) => (
        <div className="flex gap-2 justify-end">
          {canInvite && !u.is_staff && (
            <button onClick={() => resendInvite(u)} className="btn-secondary text-xs" type="button" title="Resend invite email">
              <Mail size={14} /> Resend
            </button>
          )}
          <button onClick={() => toggleActive(u)} className="btn-secondary text-xs" type="button">
            {u.is_active ? "Deactivate" : "Activate"}
          </button>
          {!u.is_staff && (user.is_staff || !!user.role?.can_manage_users) && u.id !== user.id && (
            <button
              onClick={() => deleteUser(u)}
              className="btn-secondary text-xs text-red-700 hover:bg-red-50 hover:border-red-300"
              type="button"
              title="Delete user from UmdoniWay + External ID"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Users"
        subtitle={`${users.length} ${users.length === 1 ? "user" : "users"} in organisation`}
        actions={
          canInvite && (
            <button className="btn-primary" type="button" onClick={() => setOpen(true)}>
              <UserPlus size={16} /> Invite user
            </button>
          )
        }
      />
      {loading ? <LoadingState /> : <DataTable columns={columns} rows={users} />}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title="Invite user"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={submit}>Send invite</button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" {...form.register("email", { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">First name</label>
              <input className="input" {...form.register("first_name")} />
            </div>
            <div>
              <label className="label">Last name</label>
              <input className="input" {...form.register("last_name")} />
            </div>
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" {...form.register("phone")} />
          </div>
          <div>
            <label className="label">Role</label>
            <select className="input" {...form.register("role_id", { required: true })}>
              <option value="">Select role…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </form>
      </Drawer>
    </AppLayout>
  );
}
